<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NDrawer, NDrawerContent, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import AgentSessionList from './AgentSessionList.vue'
import AgentSessionDevcontainerModal from './AgentSessionDevcontainerModal.vue'
import AgentTranscript from './AgentTranscript.vue'
import AgentComposer from './AgentComposer.vue'
import AgentSessionHeader from './AgentSessionHeader.vue'
import AgentWorkspacePanel from './AgentWorkspacePanel.vue'
import DispatchTaskModal from './DispatchTaskModal.vue'
import { useAgentStore, type AgentSessionSummary } from '../stores/agent'
import { useAgentChannelsStore } from '../stores/agent_channels'
import { useAgentSessionSlotsStore } from '../stores/agent_session_slots'
import { useAgentPromptsStore } from '../stores/agent_prompts'
import { useSessionStore } from '../stores/session'
import { useIsMobile } from '../composables/useIsMobile'
import { useSlotUsagePolling } from '../composables/useSlotUsagePolling'
import { useAgentSessionActions } from '../composables/useAgentSessionActions'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'

// Admin-only page (gated at the router) to drive cloud agent sessions from
// the browser, the web sibling of the Discord agent channel.
// Lists sessions across channels, starts/continues web sessions; the chat
// transcript itself lives in AgentTranscript.
const store = useAgentStore()
const slotUsageStore = useAgentSessionSlotsStore()
const promptsStore = useAgentPromptsStore()
const session = useSessionStore()
const message = useMessage()
const isMobile = useIsMobile()
const route = useRoute()
const router = useRouter()
useDocumentTitle('Agent')

// On mobile the session list collapses into a left drawer (mirrors
// ThreadDrawer); on desktop it stays an inline sidebar.
const sessionsOpen = ref(false)
const SESSIONS_DRAWER_WIDTH = '80%'

// Restore focus to the Sessions toggle when the mobile drawer closes.
useRestoreFocusOnClose(sessionsOpen)

const draft = ref('')
const repo = ref('')
const sending = ref(false)
const postToChannel = ref(false)

// Repo selector — populated from GET /api/v1/agent/repos/list on mount. That
// lightweight endpoint skips the admin page's per-repo container/CLI-version
// shell-outs (#871), so the picker stays instant. Fails silently: on error the
// dropdown is empty and the backend defaults to the registry default (superbrain).
interface RepoOption {
  slug: string
  is_default: boolean
}
const repoOptions = ref<{ label: string; value: string }[]>([])

async function fetchRepos() {
  try {
    const res = await fetch('/api/v1/agent/repos/list', { credentials: 'include' })
    if (!res.ok) return
    const data: { repos: RepoOption[] } = await res.json()
    repoOptions.value = data.repos.map((r) => ({ label: r.slug, value: r.slug }))
    const def = data.repos.find((r) => r.is_default)
    if (def) repo.value = def.slug
  } catch {
    // fail silently
  }
}

// A message sent while a run is still in flight is queued server-side and
// dispatched once the current run's output lands (#1433), so sending no
// longer waits on `store.agentRunning` — only on the in-flight POST itself
// (`sending`, which flips back the instant it returns).
const canSend = computed(() => draft.value.trim().length > 0 && !sending.value)

// The open session's summary row — drives the read-only context line so the
// repo/thread in scope stays visible after the start form is replaced.
const activeSession = computed(() => store.sessions.find((s) => s.thread_id === store.activeThreadId) ?? null)
const channelsStore = useAgentChannelsStore()
const homeChannelName = computed(
  () => channelsStore.channels.find((c) => c.id === activeSession.value?.home_channel_id)?.name ?? null,
)

// The open session is "working" if THIS client has a run in flight (local,
// immediate) OR the brain reports the session running from another device
// (payload status) — so the "Agent is working…" cue shows on mobile while
// the run is driven from desktop, not just on the client that started it.
const activeRunning = computed(() => store.agentRunning || activeSession.value?.running === true)

// A 401 against the agent endpoint flips needsAuth — re-check the current user
// so App.vue's auth gate takes over, the same watchdog ReviewQueueShell uses.
watch(
  () => store.needsAuth,
  (value) => {
    if (value) session.fetchCurrentUser()
  },
)

function openSession(threadId: string) {
  store.openSession(threadId)
  sessionsOpen.value = false // close the drawer after picking, on mobile
  // Reflect the open session in the URL so a reload restores it.
  if (route.params.threadId !== threadId) router.replace({ name: 'agent', params: { threadId } })
}

function newSession() {
  store.newSession()
  draft.value = ''
  sessionsOpen.value = false
  if (route.params.threadId) router.replace({ name: 'agent', params: {} })
}

// Row-action handlers (rename/delete/stop + toasts) live in the composable.
const { notifyError, renameSession, deleteSession, stopSession } = useAgentSessionActions(
  store,
  slotUsageStore,
  message,
)

// Devcontainer info modal (#1501) — the selected row's summary drives both
// visibility and the modal's title; closing clears it back to null.
const infoSession = ref<AgentSessionSummary | null>(null)

// The Sessions column's amber Dispatch task opens the same routine-dispatch
// modal the command palette does (RouteShell owns that copy's state).
const dispatchOpen = ref(false)

// One binding for both mounts of the session list (desktop sidebar, mobile drawer).
const listProps = computed(() => ({
  sessions: store.sessions,
  activeThreadId: store.activeThreadId,
  isEmpty: store.isEmpty,
  slotUsage: slotUsageStore.usage,
  stoppingThreadId: store.stoppingThreadId,
}))
const listHandlers = {
  open: openSession,
  'new-session': newSession,
  dispatch: () => (dispatchOpen.value = true),
  rename: renameSession,
  delete: deleteSession,
  stop: stopSession,
  info: (s: AgentSessionSummary) => (infoSession.value = s),
}

async function cancelRun() {
  try {
    await store.cancelRun()
  } catch (err) {
    notifyError(err, 'Failed to cancel run')
  }
}

async function send() {
  if (!canSend.value) return
  const text = draft.value.trim()
  sending.value = true
  try {
    if (store.activeThreadId) {
      await store.sendMessage(text, postToChannel.value)
    } else {
      await store.startSession(text, repo.value)
    }
    draft.value = ''
    // Sending is what claims a devcontainer slot on the box (#1380) — refresh
    // the indicator now; the mount-time poll trues up any race with the claim.
    slotUsageStore.fetchUsage().catch(() => {})
  } catch (err) {
    notifyError(err, 'Failed to send')
  } finally {
    sending.value = false
  }
}

// Slot usage: fetched on mount + a 60s poll while the page is open, so
// releases the brain can't see (the box's idle reaper) still show up (#1380).
useSlotUsagePolling(slotUsageStore)

onMounted(() => {
  // Fire-and-forget; useAsyncResource sets loadError for the template, and we
  // swallow the rethrow so a failed load doesn't surface as an unhandled
  // rejection.
  if (!store.loaded) store.fetchSessions().catch(() => {})
  fetchRepos()
  promptsStore.fetchAll().catch(() => {})
  // Live cross-device run status.
  store.subscribeStatus()
  // Live session-list updates: new sessions + metadata changes.
  store.subscribeSessionsList()
  // Restore the session named in the URL so a reload reopens it.
  const threadId = route.params.threadId
  if (typeof threadId === 'string' && threadId) store.openSession(threadId)
})
</script>

<template>
  <!-- Full-height layout: Sessions live in the shell's side-panel slot on
       desktop and a drawer on mobile; the conversation (header, transcript,
       composer) fills the middle with its own scroller, and the Workspace
       panel sits on the right on wide screens. The shell's padding is
       dropped so the header and composer run edge to edge. -->
  <RouteShell :show-sidebar="!isMobile" :main-scroll="false" main-class="p-0!">
    <template v-if="!isMobile" #sidebar>
      <aside class="w-[260px] shrink-0 overflow-y-auto border-r border-border-divider px-3 py-5">
        <AgentSessionList v-bind="listProps" v-on="listHandlers" />
      </aside>
    </template>

    <div class="flex h-full min-h-0">
      <div class="flex min-w-0 flex-1 flex-col">
        <AgentSessionHeader
          :active-thread-id="store.activeThreadId"
          :session="activeSession"
          :running="activeRunning"
          :stopping="!!store.activeThreadId && store.stoppingThreadId === store.activeThreadId"
          :is-mobile="isMobile"
          :sessions-open="sessionsOpen"
          @stop="stopSession"
          @open-sessions="sessionsOpen = true"
        />

        <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
          <NSpin />
        </div>

        <div
          v-else-if="store.loadError"
          class="px-4 py-4 text-sm text-status-alert sm:px-7"
          data-testid="agent-load-error"
        >
          Failed to load sessions: {{ store.loadError }}
        </div>

        <div
          v-else-if="store.needsAuth"
          class="px-4 py-4 text-sm text-ink-muted sm:px-7"
          data-testid="agent-needs-auth"
        >
          Session expired — sign in again to continue.
        </div>

        <div v-else class="flex min-h-0 flex-1 flex-col" data-testid="agent-surface">
          <AgentTranscript
            :messages="store.messages"
            :agent-running="activeRunning"
            :has-active-thread="!!store.activeThreadId"
            :live-status="store.liveStatus"
          />

          <AgentComposer
            v-model:repo="repo"
            v-model:draft="draft"
            v-model:post-to-channel="postToChannel"
            :home-channel-name="homeChannelName"
            :repo-options="repoOptions"
            :prompts="promptsStore.prompts"
            :is-new-session="!store.activeThreadId"
            :can-send="canSend"
            :sending="sending"
            :active-running="activeRunning"
            :agent-running="store.agentRunning"
            @send="send"
            @cancel="cancelRun"
          />
        </div>
      </div>

      <AgentWorkspacePanel
        v-if="store.activeThreadId && !isMobile"
        class="max-xl:hidden"
        :thread-id="store.activeThreadId"
        :session="activeSession"
      />
    </div>

    <!-- Mobile: Sessions in a left drawer behind the header toggle.
         aria-label names the dialog for AT; the id is the toggle's
         aria-controls target. -->
    <NDrawer v-if="isMobile" v-model:show="sessionsOpen" :width="SESSIONS_DRAWER_WIDTH" placement="left">
      <NDrawerContent id="agent-sessions-panel" aria-label="Sessions" :native-scrollbar="false" closable>
        <AgentSessionList v-bind="listProps" v-on="listHandlers" />
      </NDrawerContent>
    </NDrawer>

    <AgentSessionDevcontainerModal :session="infoSession" @close="infoSession = null" />
    <DispatchTaskModal v-model:show="dispatchOpen" />
  </RouteShell>
</template>
